import { z } from "zod";

import { isIdentityCredentialKey } from "../../../../shared/core/identity-credential-keys";
import { normaliseKeySpelling } from "../../../../shared/core/key-spelling";

/**
 * The browser chat surface of the Public API v1.
 *
 * The wire EVENTS this surface streams live in `chat-stream.schemas.ts`. This
 * file holds the REQUEST side — what a caller sends — and nothing else.
 */

/**
 * One part of an AI SDK `UIMessage`.
 *
 * Declared only as far as this server READS it, which is the `text` arm. A
 * stock `useChat` also sends `file`, `reasoning`, `tool-*` and `data-*` parts
 * in its history; they are accepted as opaque objects carrying a `type` and
 * ignored, because this server holds the conversation and never reconstructs
 * it from a client-supplied array.
 *
 * 🔴 NOT `.strict()`, and that is the one place in this file where leniency is
 * correct. These objects are AUTHORED BY THE SDK, not by the customer, so a
 * refusal here would be this server refusing its own client library the day
 * `ai@7` adds a field to a part it already sends. The BODY is strict; the parts
 * inside a field the server ignores are not.
 */
const UiMessagePartSchema = z.object({
  type: z.string().min(1),
  text: z.string().optional()
});

/**
 * One message of the AI SDK's client-side history.
 *
 * `role` is a bare `z.string()` rather than an enum: it is read only to find
 * the last `"user"` entry, and an unknown role is simply not that.
 */
const UiMessageSchema = z.object({
  id: z.string().optional(),
  role: z.string().min(1),
  parts: z.array(UiMessagePartSchema).optional()
});

/**
 * The largest turn this surface runs, in characters — whichever dialect carried
 * it.
 *
 * 🔴 A `.max()` ON `content` ALONE IS A CAP ON THE DOOR NOBODY USES. `content`
 * is the plain-`fetch` door; the route exists to serve a stock `useChat`, which
 * sends `messages`. Nothing inside that array bounds a string: the parts are
 * deliberately lenient (they are authored by the SDK), so the only limit on the
 * `messages` path was the express body limit — 25 MB against this 100,000, two
 * orders of magnitude apart on the path the feature is FOR.
 *
 * So the cap belongs on the EXTRACTED TEXT, which is the one value both dialects
 * produce, and it is exported because the refusal has to happen where a status
 * code can still be sent. Refuse, never truncate: a silently shortened prompt is
 * answered confidently and wrongly, and the caller has no way to tell.
 */
export const SEND_CHAT_MESSAGE_MAX_TEXT_LENGTH = 100_000;

/**
 * Most `metadata` entries a caller may attach to a conversation.
 *
 * 🔴 THESE THREE NUMBERS ARE NOT CHOSEN HERE. They are
 * `PROMPT_METADATA_MAX_ENTRIES` / `_MAX_KEY_LEN` / `_MAX_VALUE_LEN` from
 * `apps/backend/src/_common/prompt/prompt-metadata.ts`, which is the step that
 * renders this map into an agent's SYSTEM prompt and is today the only bound
 * this channel has anywhere. Restating them rather than importing them is
 * forced: `@nexus/types` cannot import from the backend, and this bound has to
 * be in the CONTRACT so the refusal reaches the caller as a 400.
 *
 * A copy is a thing that drifts, so it is pinned rather than trusted —
 * `apps/backend/src/public/v1/__tests__/chat-metadata-bounds-match-the-prompt-fence.spec.ts`
 * asserts each constant here equals its prompt-side twin, in the one package
 * that can see both. Widening either side alone is a RED, which is the only
 * mechanism that can hold two numbers equal across a package boundary.
 *
 * ## Why the door and the fence carry the SAME number, rather than the door being tighter
 *
 * A tighter door would refuse text the fence would have rendered happily, which
 * makes this route narrower than the console widget it replaces for no security
 * gain. A looser one would accept text the fence then silently truncates — the
 * failure this whole file argues against on `content` above. Equal is the only
 * value where "accepted" and "reaches the model intact" are the same set.
 */
export const CHAT_METADATA_MAX_ENTRIES = 20;

/** Longest `metadata` KEY, in characters. The prompt fence truncates at the same number. */
export const CHAT_METADATA_MAX_KEY_LENGTH = 64;

/**
 * Longest `metadata` VALUE, in characters. The prompt fence truncates at the
 * same number.
 *
 * 🔴 UTF-16 CODE UNITS, NOT BYTES — the unit `.slice()` and `.max()` both work
 * in, and the reason there is no total-size bound on this field:
 *
 * debt: this door bounds CARDINALITY (20) and PER-VALUE LENGTH (512 code
 *       units) and does not bound the block's total BYTES. A caller sending 20
 *       maximal values of 4-byte characters emits roughly 40 KB of system
 *       prompt where the same shape in ASCII emits roughly 12 KB.
 *       Upgrade trigger: `prompt-metadata.ts` gaining a byte budget of its
 *       own. A byte bound added HERE and not THERE is the drift
 *       `chat-metadata-bounds-match-the-prompt-fence.spec.ts` exists to
 *       refuse, and would make this route reject text the fence renders
 *       happily — so the two move together or not at all.
 *
 * `agent-memory-document.ts` is the worked example of the other choice and of
 * why it is not available here: it renders into the same sink under a real
 * 8,000-BYTE budget, so it measures what it emits and drops whole entries. It
 * can do that because it owns both ends. This door does not own the fence.
 */
export const CHAT_METADATA_MAX_VALUE_LENGTH = 512;

/**
 * Identifier-like keys, and the reason this route may enforce what the render
 * step cannot.
 *
 * `PROMPT_METADATA_KEY_REGEX` carries this same expression and its docblock
 * states the asymmetry outright: *"A write door can refuse a key outright
 * because the caller is still there to be told. The compose step cannot: a key
 * already stored is a key some tenant may be relying on."* This is that write
 * door, so it refuses. `sanitizeRedirectMetadata` is the shipped precedent — it
 * applies the identical regex to the WhatsApp channel's own write side.
 */
export const CHAT_METADATA_KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Keys a caller may NOT set, because on this route the server already knows the
 * answer and its answer is verified.
 *
 * 🔴 THIS IS THE ONE PLACE THE v1 DOOR IS DELIBERATELY NARROWER THAN THE
 * CONSOLE WIDGET, AND IT IS NARROWER IN THE DIRECTION OF PARITY RATHER THAN
 * AWAY FROM IT. The console widget reserves exactly these seven names already —
 * `RESERVED_IDENTITY_KEYS` in `apps/frontend/src/common/services/chat/chatApi.ts`,
 * derived from `EMBED_URL_KEY_MAP`, which drops any of them appearing in the
 * host page's `sdata` blob. But that filter runs IN THE BROWSER, so it binds
 * only a page that uses the shipped widget: anything posting the HTTP body
 * directly bypasses it entirely. Moving the same seven names server-side is the
 * same rule with teeth.
 *
 * The reason they must not be caller-set HERE is stronger than on the widget.
 * A v1 visitor's identity is the session token's `externalUserId` claim, which
 * `CreateAnonymousChatSessionUseCase` sets only after an HMAC verifies. A
 * caller-typed `external_user_id` would land in the same *Additional Data*
 * block as that verified claim, in the same system prompt, with nothing to tell
 * the model which of the two is authoritative — an identity assertion wearing a
 * metadata costume.
 *
 * ⚠️ REFUSED, NOT DROPPED. A silently stripped identity key is a 200 for a
 * request that did something else, which is the argument `.strict()` gets on
 * the mint body's `identityHash` one file over.
 *
 * 🔴 EVERY NAME IS REFUSED IN EVERY SPELLING, NOT ONLY AS THE LITERAL BELOW.
 * `CHAT_METADATA_KEY_REGEX` admits case and `_` freely, so `externalUserId`,
 * `customerEmail` and `CUSTOMER_NAME` all reach the same *Additional Data* block
 * as the verified claim and read to the model as the same assertion. A key is
 * compared by `normaliseKeySpelling` (`shared/core/key-spelling.ts`) against the
 * normalised names, so the door refuses a spelling of a reserved name exactly as
 * it refuses the name. The identity credential is additionally refused under
 * every name `isIdentityCredentialKey` knows (`identityHash`), because it is a
 * bearer credential and every sink downstream drops it on the same predicate.
 * Whole names only: `customer_email_verified` and `my_external_user_id` are
 * ordinary context and pass.
 */
export const CHAT_METADATA_RESERVED_KEYS = [
  "external_user_id",
  "user_hash",
  "customer_id",
  "customer_name",
  "customer_email",
  "customer_phone",
  "customer_attributes"
] as const;

/** The reserved names in `normaliseKeySpelling` form — what a caller's key is compared against. */
const RESERVED_KEY_SPELLINGS: ReadonlySet<string> = new Set<string>(
  CHAT_METADATA_RESERVED_KEYS.map(normaliseKeySpelling)
);

/** True when `key` is a reserved identity name or the identity credential, in any spelling. */
function isReservedChatMetadataKey(key: string): boolean {
  return RESERVED_KEY_SPELLINGS.has(normaliseKeySpelling(key)) || isIdentityCredentialKey(key);
}

/**
 * Host-page context a caller attaches to the conversation, as a FLAT STRING MAP.
 *
 * ## The shape is the console widget's, deliberately unenriched
 *
 * `chatApi.ts` keeps strings verbatim, `String()`s numbers and booleans, and
 * DROPS `null`, objects and arrays rather than stringifying them. That last
 * choice is load-bearing and is why this schema takes `z.string()` and not
 * `z.unknown()`: `String(null)` is `"null"`, which no consumer can tell from a
 * customer who typed the word. A caller coerces on its own side, exactly as the
 * widget does, and a non-string here is a 400 rather than a silent `"[object
 * Object]"` in somebody's prompt.
 *
 * `z.record` VALIDATES keys in zod 4 rather than stripping them — measured on
 * 4.1.13, where a key failing this regex, an over-long key and a non-string
 * value are each a refusal, and `{}` still parses. So the key rules below are
 * enforced by the parse and need no separate pass.
 */
export const SendChatMessageMetadataSchema = z
  .record(
    z.string().max(CHAT_METADATA_MAX_KEY_LENGTH).regex(CHAT_METADATA_KEY_REGEX),
    z.string().max(CHAT_METADATA_MAX_VALUE_LENGTH)
  )
  .refine((value) => Object.keys(value).length <= CHAT_METADATA_MAX_ENTRIES, {
    message: `metadata may carry at most ${CHAT_METADATA_MAX_ENTRIES} entries`
  })
  .refine((value) => !Object.keys(value).some(isReservedChatMetadataKey), {
    message: `metadata may not carry a reserved identity key in any spelling (${CHAT_METADATA_RESERVED_KEYS.join(", ")}, or identityHash)`
  });

/**
 * Body of `POST /public/v1/deployments/:deploymentId/chat`.
 *
 * ## It must accept a stock `useChat` POST unchanged
 *
 * `DefaultChatTransport` sends `{ id, messages, trigger, messageId }` with no
 * `prepareSendMessagesRequest` override. All four are declared here so a
 * customer can point `useChat({ api })` at this route and have it work with no
 * configuration — and so that `.strict()` below refuses a MISSPELLED key rather
 * than the SDK's own vocabulary.
 *
 * `content` is the non-AI-SDK door: a plain `fetch` caller sends one string and
 * nothing else.
 *
 * ## 🔴 THE SERVER READS ONLY THE LAST USER MESSAGE, AND `messages` IS OTHERWISE
 * IGNORED
 *
 * The conversation lives in this database. `messages` is validated for SHAPE
 * and then used for exactly one thing: extracting the text the visitor just
 * typed, when `content` was not supplied. Reconstructing history from it would
 * make the transcript a client-supplied value — the largest prompt-injection
 * surface a headless chat API can offer, and a tenancy hole besides, since the
 * token names the conversation and the body must never be able to rename it.
 *
 * There is deliberately NO `chatId` field for the same reason: the conversation
 * is named by the session token's own claim, resolved server-side at mint.
 *
 * ## `.strict()`
 *
 * `z.object` STRIPS an unknown key and reports success, so a caller sending
 * `knowledgIds` (one letter short) would get a 200 for a request that silently
 * did something else. `unknown-key-strictness.test.ts` holds every v1 write
 * body to this by default.
 */
const SendChatMessageBodyObjectSchema = z
  .object({
    /**
     * The message text, for a caller that is not the AI SDK.
     *
     * When present it WINS over `messages` — an explicit value beats one
     * extracted from a history array.
     */
    content: z.string().max(SEND_CHAT_MESSAGE_MAX_TEXT_LENGTH).optional(),

    /** The AI SDK's client-side history. Read only for its last user message. */
    messages: z.array(UiMessageSchema).max(1000).optional(),

    /**
     * The AI SDK's own conversation id. Accepted and IGNORED: the conversation
     * this request writes to is the session token's `chatId` claim, which no
     * request body can move.
     */
    id: z.string().max(255).optional(),

    /** `"submit-message"` / `"regenerate-message"`. Accepted and ignored. */
    trigger: z.string().max(64).optional(),

    /** The AI SDK's id for the message being sent. Accepted and ignored. */
    messageId: z.string().max(255).optional(),

    /** Knowledge documents to attach to this turn. */
    knowledgeIds: z.array(z.string().uuid()).max(50).optional(),

    /** Image URLs to attach to this turn. */
    images: z.array(z.string().max(2048)).max(10).optional(),

    /**
     * Host-page context for the agent — the headless equivalent of the console
     * widget's `embedData`.
     *
     * 🔴 READ ONLY ON THE TURN THAT CREATES THE CONVERSATION, AND IGNORED ON
     * EVERY TURN AFTER IT. A chat-session token names ONE conversation, and
     * that conversation is created by the first `POST …/chat` the token makes;
     * this map is written into its `settings.embedData` at that moment and is
     * never rewritten. Three reasons, and the third is the one that is not
     * obvious:
     *
     * 1. It mirrors the console path, where the widget attaches `embedData` at
     *    `POST /api/chatrooms` — session start — and not per message.
     * 2. Re-writing it per turn is a `Chat` row update per turn, for data that
     *    is overwhelmingly session-stable (the live production keys are a user
     *    identifier and a shop identifier).
     * 3. It is part of the Anthropic CACHE PREFIX. `chatMetadataBlocks` feeds
     *    `wrapDynamic`, and `inference-agent.class.ts` records that `embedData`
     *    already varies per CHAT against a prefix that is per AGENT. Letting it
     *    vary per TURN would invalidate that prefix on every turn of every
     *    conversation that sends the field.
     *
     * So a caller may send it on every request — a stock `useChat` `body`
     * option is static — and only the first one is read. It is accepted and
     * ignored thereafter, exactly as `id`, `trigger` and `messageId` are.
     *
     * ⚠️ IT IS NOT SENT ON THE MINT, WHICH IS WHERE IT LOOKS LIKE IT BELONGS.
     * The anonymous mint deliberately writes NO ROW — that is what makes a mint
     * flood cost one indexed read and nothing else — so it has nowhere to put
     * this, and the session token is explicitly a credential that "carries no
     * scopes, no capabilities and no settings snapshot". A 20×512 map is also
     * ~12 KB, which does not fit in the request header the token travels in.
     */
    metadata: SendChatMessageMetadataSchema.optional()
  })
  .strict();

export type SendChatMessageBody = z.infer<typeof SendChatMessageBodyObjectSchema>;

/**
 * The text a turn should run on, from a body that may have arrived in either
 * dialect.
 *
 * Exported from `@nexus/types` rather than written in the controller so the
 * rule is one object the contract and the handler share, and so a spec can
 * drive it without booting Nest.
 *
 * Returns `""` when neither dialect supplied anything — the caller decides what
 * an empty turn means, because "refuse it" and "let the agent answer a bare
 * attachment" are both defensible and only the route knows which.
 */
export function extractSendChatMessageText(body: SendChatMessageBody): string {
  if (body.content !== undefined && body.content.length > 0) return body.content;

  const messages = body.messages ?? [];
  // The LAST user message, not the first and not the last message: a stock
  // `useChat` appends the new turn to the whole history it holds, and the
  // trailing entry is occasionally an assistant placeholder.
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message === undefined || message.role !== "user") continue;
    return (message.parts ?? [])
      .filter((part) => part.type === "text")
      .map((part) => part.text ?? "")
      .join("");
  }

  return "";
}

/**
 * The body a caller may send, INCLUDING the size of the turn it asks for.
 *
 * The cap is a refinement rather than a `.max()` on a field because the value it
 * bounds is not a field: it is whatever `extractSendChatMessageText` returns,
 * and that comes from `content` on one path and from a join over `messages` on
 * the other. Bounding only `content` caps the door the route is not for.
 *
 * 🔴 IT LIVES HERE, NOT IN THE HANDLER, so no route can forget it. The handler
 * refuses an EMPTY turn — that one is genuinely the route's call, because
 * "answer a bare attachment" is defensible — but a turn nobody can afford to run
 * is a property of the body, and a property of the body belongs to the body's
 * schema.
 *
 * `.strict()` survives the wrapper: `unknown-key-strictness.test.ts` probes with
 * an unknown KEY through `safeParse` rather than reading `def.unknownKeys`, and
 * the refinement delegates to the object it wraps.
 */
export const SendChatMessageBodySchema = SendChatMessageBodyObjectSchema.refine(
  (body) => extractSendChatMessageText(body).length <= SEND_CHAT_MESSAGE_MAX_TEXT_LENGTH,
  {
    path: ["messages"],
    message: `A message may carry at most ${SEND_CHAT_MESSAGE_MAX_TEXT_LENGTH} characters`
  }
);

// ============================================================================
// Resume · stop · status — the durable half of the browser chat surface
// ============================================================================

/**
 * How a turn ended, as the wire spells it.
 *
 * Mirrors the backend's `ChatTurnOutcome`, and the two are joined by
 * `chat-turn-outcome-matches-the-contract.spec.ts` rather than by hope: this
 * package cannot import from `apps/backend`, so a duplicated closed set needs
 * an assertion or it drifts the first time a fourth outcome is added.
 *
 * `stopped` is the member that matters here. `finish.finishReason` maps it to
 * the SDK's `other`, which is a poorer word — a client that needs to tell "the
 * visitor pressed Stop" from "the agent errored" reads it from this surface.
 */
export const CHAT_TURN_OUTCOMES = ["completed", "failed", "stopped"] as const;
export const ChatTurnOutcomeSchema = z.enum(CHAT_TURN_OUTCOMES);

/**
 * The body of `POST /public/v1/deployments/:deploymentId/chat/stop`.
 *
 * 🔴 THE CONVERSATION IS NOT IN IT, AND MUST NEVER BE. It is the chat-session
 * token's own `chatId` claim, resolved server-side at mint. A conversation id in
 * a browser-supplied position is precisely the cross-tenant hole the claim
 * exists to close — the same reason it is absent from the send route's body.
 */
export const StopChatTurnBodySchema = z
  .object({
    /**
     * The turn to stop.
     *
     * Optional because a visitor pressing Stop knows about the answer on screen
     * and not about a generation id; omitted, the newest unsettled turn is
     * taken. Supplying it is strictly safer — a stale stop cannot reach a turn
     * that started after the client last looked.
     */
    turnId: z.string().min(1).optional()
  })
  .strict();

export const StopChatTurnResponseSchema = z.object({
  /**
   * Whether a live turn was found to address the stop TO.
   *
   * 🔴 **NOT "THE TURN HAS STOPPED", AND THE DIFFERENCE IS FORCED BY THE
   * TRANSPORT.** The stop reaches the pod running the generation through a
   * fire-and-forget publish, so no value this request can compute knows whether
   * it landed. Poll `chat/status` and read `outcome === "stopped"` for that.
   */
  accepted: z.boolean(),
  /** The turn the stop was addressed to, or `null` when none was running. */
  turnId: z.string().nullable()
});

export const ChatTurnStatusResponseSchema = z.object({
  /** The newest turn of this conversation, or `null` for one that has none. */
  turnId: z.string().nullable(),
  /**
   * `true` while nothing has recorded the turn ending.
   *
   * ⚠️ That is a statement about the RECORD, not a heartbeat: a turn whose pod
   * died before it could settle also reads `true`, for ever. Nothing on this
   * surface can tell the two apart, and reporting a live turn as finished would
   * be the worse error.
   */
  running: z.boolean(),
  /** How it ended, once it has. `null` while it is running. */
  outcome: ChatTurnOutcomeSchema.nullable(),
  /**
   * The cursor of the newest frame recorded — what to send as `Last-Event-ID`
   * when resuming. `null` for a turn that has produced no frame yet.
   */
  lastEventId: z.string().nullable(),
  /** How many frames the turn has produced so far. */
  frameCount: z.number().int().nonnegative()
});
